import type { ReactNode } from 'react';
import { cn } from './cn';

export type Tone =
  | 'neutral'
  | 'info'
  | 'active'
  | 'warning'
  | 'attention'
  | 'positive'
  | 'success'
  | 'muted'
  | 'critical'
  | 'accent'
  | 'dept-apd'
  | 'dept-ccso'
  | 'dept-gsp'
  | 'dept-ccfr';

/**
 * Tone styling, used by every status and priority indicator.
 *
 * Each tone pairs a background, a border and a foreground — never a background
 * alone — so the contrast holds in both themes.
 */
export const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-neutral-soft text-neutral-tone border-neutral-border',
  info: 'bg-info-soft text-info-tone border-info-border',
  active: 'bg-active-soft text-active-tone border-active-border',
  warning: 'bg-warning-soft text-warning-tone border-warning-border',
  attention: 'bg-attention-soft text-attention-tone border-attention-border',
  positive: 'bg-positive-soft text-positive-tone border-positive-border',
  success: 'bg-success-soft text-success-tone border-success-border',
  muted: 'bg-muted-soft text-muted-tone border-muted-border',
  critical: 'bg-critical-soft text-critical-tone border-critical-border',
  accent: 'bg-accent-soft text-accent-text border-accent-border',
  'dept-apd': 'bg-dept-apd-soft text-dept-apd-tone border-dept-apd-border',
  'dept-ccso': 'bg-dept-ccso-soft text-dept-ccso-tone border-dept-ccso-border',
  'dept-gsp': 'bg-dept-gsp-soft text-dept-gsp-tone border-dept-gsp-border',
  'dept-ccfr': 'bg-dept-ccfr-soft text-dept-ccfr-tone border-dept-ccfr-border',
};

export const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-neutral-tone',
  info: 'bg-info-tone',
  active: 'bg-active-tone',
  warning: 'bg-warning-tone',
  attention: 'bg-attention-tone',
  positive: 'bg-positive-tone',
  success: 'bg-success-tone',
  muted: 'bg-muted-tone',
  critical: 'bg-critical-tone',
  accent: 'bg-accent',
  'dept-apd': 'bg-dept-apd-tone',
  'dept-ccso': 'bg-dept-ccso-tone',
  'dept-gsp': 'bg-dept-gsp-tone',
  'dept-ccfr': 'bg-dept-ccfr-tone',
};

export interface BadgeProps {
  tone?: Tone;
  icon?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
  title?: string;
  children: ReactNode;
}

export function Badge({ tone = 'neutral', icon, size = 'md', className, title, children }: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border font-medium whitespace-nowrap',
        size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-xs',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/** A quieter label for metadata: project, category, tag. */
export function Chip({ children, className, title }: { children: ReactNode; className?: string; title?: string }) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded border border-border bg-surface-sunken px-1.5 py-0.5',
        'text-[11px] text-text-muted whitespace-nowrap',
        className,
      )}
    >
      {children}
    </span>
  );
}
